import "./sms-configuration.module.css";

import { useState, useEffect, useCallback } from "react";
import { ButtonGroup } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { filterData, SearchType } from "filter-data";
import { cloneDeep } from "lodash";

// Hook
import useStickyHeader from "hooks/useStickyHeader";
import usePageContext from "hooks/usePageContext";
import useTable from "hooks/useTable";
import useMessage from "hooks/useMessage";

// Slice
import { uiActions } from "stores/slice/ui";

// Const
import { organizationType } from "const/nuclias/organization";
import {
  defaultPathList,
  defaultSearch, searchType,
  tableColumn, defaultModalStatus
} from "./const";

// Component
import Breadcrumbs from "components/Breadcrumb";
import ButtonWithIcon from "components/ButtonWithIcon";
import MessageBoxGroup from "components/MessageBoxGroup";
import DropdownWithItem from "components/DropdownWithItem";
import DropdownWithAdvancedSearch from "components/DropdownWithAdvancedSearch";

import SmsConfigurationTable from "./components/SmsConfigurationTable";
import SmsConfigurationPagination from "./components/SmsConfigurationPagination";
import AddOrEditSmsAccountModal from "./modals/AddOrEditSmsAccountModal";
import DeleteModal from "./modals/DeleteModal";

// Controller
import controller from "./controller";

const SmsConfiguration = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // State
  const [modalStatus, setModalStatus] = useState({ ...defaultModalStatus });
  const [search, setSearch] = useState(cloneDeep(defaultSearch));
  const [originSmsConfigList, setOriginSmsConfigList] = useState([]);
  const [filteredSmsConfigList, setFilteredSmsConfigList] = useState([]);
  const [currentSmsConfigList, setCurrentSmsConfigList] = useState([]);
  const [selectedSmsConfig, setSelectedSmsConfig] = useState(null);
  const [deletedSmsConfigId, setDeletedSmsConfigId] = useState(null);

  // Hook
  const { tableRef, stickyStyle } = useStickyHeader();
  const {
    orgTreeMap,
    isOrgLevel, selectedOrg,
    getManagedSiteTagList, getManagedSiteList, getAccessLevel
  } = usePageContext();
  const {
    messages, messagesType,
    commonErrorCode, resetMessages, setErrorMessage
  } = useMessage();
  const {
    selectedPage, selectedPageLimit,
    tableData, getTableHeader,
    changePage, changePageLimit, getCurrentPageData
  } = useTable(filteredSmsConfigList, tableColumn);

  // Method
  const init = useCallback(async () => {
    resetMessages();

    if (!isOrgLevel) {
      setErrorMessage(commonErrorCode.forbidMspOrg);
      return;
    }

    try {
      dispatch(uiActions.showSpinner());

      const response = await controller.getSmsConfig(selectedOrg.id);
      if (response.error) {
        throw response.error;
      }

      const smsConfigList = controller.parseRawData(response.data, orgTreeMap, selectedOrg);
      setOriginSmsConfigList(smsConfigList);
      setFilteredSmsConfigList(smsConfigList);
      setCurrentSmsConfigList(getCurrentPageData());

      dispatch(uiActions.hideSpinner());
    } catch (error) {
      setErrorMessage(error.code);
      dispatch(uiActions.hideSpinner());
    }
  }, [selectedOrg]);

  const changeModalStatus = useCallback((type, value) => {
    const updatedModalStatus = cloneDeep(modalStatus);
    updatedModalStatus[type].status = value;
    setModalStatus(updatedModalStatus);
  }, [modalStatus]);

  const cleanSearch = () => {
    setSearch(cloneDeep(defaultSearch));
    setFilteredSmsConfigList(originSmsConfigList);
  }

  const getDropdownSiteTagList = () => {
    const dropdownSiteTagList = cloneDeep(controller.getDefaultSiteTagList(true, getManagedSiteTagList(), selectedSmsConfig));

    if (!search.siteTag) {
      return dropdownSiteTagList;
    }

    for (const siteTag of dropdownSiteTagList) {
      siteTag.isActive = false;
      if (search.siteTag.toLowerCase() === siteTag.title.toLowerCase()) {
        siteTag.isActive = true;
      }
    }

    return dropdownSiteTagList;
  }

  const getDropdownSiteList = () => {
    const dropdownSiteList = cloneDeep(controller.getDefaultSiteList(true, getManagedSiteList(), selectedSmsConfig));

    if (!search.site) {
      return dropdownSiteList;
    }

    for (const site of dropdownSiteList) {
      site.isActive = false;
      if (search.site.toLowerCase() === site.title.toLowerCase()) {
        site.isActive = true;
      }
    }

    return dropdownSiteList;
  }

  const handleSearch = (field, value) => {
    const udpatedSearch = cloneDeep(defaultSearch);

    switch (field) {
      case searchType.siteTag.value:
        if (value.id === 0)
          udpatedSearch.siteTag = null;
        else
          udpatedSearch.siteTag = value.title;

        udpatedSearch.site = search.site;
        break;
      case searchType.site.value:
        if (value.id === 0)
          udpatedSearch.site = null;
        else
          udpatedSearch.site = value.title;

        udpatedSearch.siteTag = search.siteTag;
        break;
      default:
        return;
    }

    setSearch(udpatedSearch);

    let searchConditionList = [];
    udpatedSearch.siteTag && searchConditionList.push({
      key: ['managedSiteTag'],
      value: udpatedSearch.siteTag,
      type: SearchType.EQ
    });

    udpatedSearch.site && searchConditionList.push({
      key: ['managedSiteSearchList'],
      value: udpatedSearch.site,
      type: SearchType.EQ
    });

    const fuzzySearchResult = filterData(originSmsConfigList, searchConditionList);
    setFilteredSmsConfigList(fuzzySearchResult);
    changePage(1);
  }

  const getSearchKeyword = (field) => {
    if (field === searchType.fuzzy.value) {
      let keywordList = [];

      if (search.siteTag) {
        const label = t(searchType.siteTag.label);
        keywordList.push(`(${label}: ${search.siteTag})`);
      }

      if (search.site) {
        const label = t(searchType.site.label);
        keywordList.push(`(${label}: ${search.site})`);
      }

      return keywordList.join(` ${t('19bf32b872')} `);
    }

    if (field === searchType.siteTag.value) {
      return search.siteTag;
    }

    if (field === searchType.site.value) {
      return search.site;
    }
  }

  // Side effect
  useEffect(() => {
    init();
  }, [init, selectedOrg]);

  useEffect(() => {
    setCurrentSmsConfigList(getCurrentPageData());
  }, [selectedPage, selectedPageLimit, tableData]);

  return (
    <>
      <Breadcrumbs pathList={defaultPathList} />

      <div className="layout-container layout-container--column layout-container--fluid">
        {!isOrgLevel &&
          (<MessageBoxGroup
            messages={messages}
            ErrorMessagesType={messagesType.error.client}
            onClose={resetMessages}
          />)
        }
        {isOrgLevel &&
          (<MessageBoxGroup
            messages={messages}
            ErrorMessagesType={messagesType.error.server}
            onClose={resetMessages}
          />)
        }

        {isOrgLevel && (
          <>
            <div className="d-flex justify-content-between">
              <ButtonGroup>
                <ButtonWithIcon
                  label="617ff5ceaf"
                  iconClassName="icon-expand"
                  onClick={() => changeModalStatus(modalStatus.addOrEdit.type, true)}
                />
              </ButtonGroup>

              {(getAccessLevel() !== organizationType.org && getAccessLevel() !== organizationType.tag) && (
                <div style={{ height: '36px' }}></div>
              )}

              {(getAccessLevel() === organizationType.org || getAccessLevel() === organizationType.tag) && (
                <DropdownWithAdvancedSearch
                  value={getSearchKeyword('fuzzy')}
                  alignEnd={true}
                  dataBsToggleOnButton={true}
                  dropdownMenuStyle={{ minWidth: 371 }}
                  buttonOnClick={cleanSearch}
                  onClick={cleanSearch}
                  dataBsToggleOnInput={true}
                  readOnly
                >
                  {getAccessLevel() === organizationType.org && (
                    <li>
                      <div className="form-title">{t('3039a00fca')}</div>
                      <DropdownWithItem
                        id="status-dropdown"
                        type="normal"
                        selectedItem={getDropdownSiteTagList().filter(siteTag => siteTag.isActive)[0]}
                        itemList={getDropdownSiteTagList()}
                        onClick={siteTag => handleSearch(searchType.siteTag.value, siteTag)}
                      />
                    </li>
                  )}
                  <li className="mt-2">
                    <div className="form-title">{t('a7d6475ec8')}</div>
                    <DropdownWithItem
                      id="status-dropdown"
                      type="normal"
                      selectedItem={getDropdownSiteList().filter(site => site.isActive)[0]}
                      itemList={getDropdownSiteList()}
                      onClick={site => handleSearch(searchType.site.value, site)}
                    />
                  </li>
                </DropdownWithAdvancedSearch>
              )}
            </div>

            <SmsConfigurationTable
              tableRef={tableRef}
              stickyStyle={stickyStyle}
              tableHeader={getTableHeader}
              selectedPage={selectedPage}
              selectedPageLimit={selectedPageLimit}
              currentSmsConfigList={currentSmsConfigList}
              modalStatus={modalStatus}
              changeModalStatus={changeModalStatus}
              selectedOrg={selectedOrg}
              selectSmsConfigCallback={smsConfig => setSelectedSmsConfig(smsConfig)}
              handleDelete={id => setDeletedSmsConfigId(id)}
              successCallback={init}
              errorCallback={error => setErrorMessage(error.code)}
            />

            <SmsConfigurationPagination
              originSmsConfigList={originSmsConfigList}
              selectedPage={selectedPage}
              setSelectedPage={changePage}
              setSelectedPageLimit={changePageLimit}
            />

            {/* Add or edit SMS account modal */}
            <AddOrEditSmsAccountModal
              modalStatus={modalStatus}
              changeModalStatus={changeModalStatus}
              selectedOrg={selectedOrg}
              managedSiteTagList={getManagedSiteTagList()}
              managedSiteList={getManagedSiteList()}
              selectedSmsConfig={selectedSmsConfig}
              successCallback={init}
            />

            {/* Delete modal */}
            <DeleteModal
              modalStatus={modalStatus}
              selectedOrg={selectedOrg}
              selectedId={deletedSmsConfigId}
              title="9c19b68222"
              description="d36caef333"
              changeModalStatus={() => changeModalStatus(modalStatus.delete.type, false)}
              execute={(orgId, idList) => controller.deleteSmsConfig(orgId, idList)}
              successCallback={init}
            />
          </>
        )}
      </div>
    </>
  );
};

export default SmsConfiguration;
